'use strict';

import * as THREE from 'three';
import gsap from 'gsap';

import {TransformControls} from "three/examples/jsm/controls/TransformControls.js";
import { isDev, projectMode, settingsData, threeJsVariables } from '../../settings';
import {
    autoRotateTimerAction,
    enableOrbitControls, getOrbitControlsDistanceAndPan, getPolarAngleFromViewMode,
    getWorldData, resetPanLimits,
    setMinAndMaxControlsDistance, setPanLimits,
    setPolarAngle, setSmoothDistance,
} from '../controls/orbitControls';
import {poiIntersection} from "../poi/poiCss";
import {animateCamera} from "../threejs/animation";
import TWEEN from "../../libs/tween.umd";
import {ViewMode} from "../../common";
import { userPrevent } from '../../functions';

const cameraTourObj = {
    action: false,
    isCameraOnPosition: false,
    time: {
        target: config.features?.projectTour?.time?.target || 30,
        camera: config.features?.projectTour?.time?.camera || 10
    },
    projectName: config?.projectFolder || 'projectName',
    cameraPoints: config.features?.projectTour?.points?.camera || [], //get from config
    targetPoints: config.features?.projectTour?.points?.target || [], //ger from config
    'CameraPoints': () => exportPoints('camera'),
    'TargetPoints': () => exportPoints('target'),
    'addPoint': () => addPoint(),
    'removePoint': () => removePoint(),
    'setCameraDistance': setCameraDistance,
};

export const cameraTour = () => {
    if (!config.features.projectTour.active || projectMode.LiteMode.isActive()) return;

    createCurveTourCamera();

    isDev && setTransformControls();
    isDev && addGuiHelper();
}

/**
 * Create routes, points and curves for tour
 */

function createCurveTourCamera() {
    const {scene} = threeJsVariables;
    const {cameraVector} = getWorldData();

    const groupCameraRoute = new THREE.Group();
    groupCameraRoute.name = 'groupCameraRoute';

    if (!cameraTourObj.cameraPoints.length) {
        cameraTourObj.cameraPoints = [
            {x: cameraVector.x, y: cameraVector.y, z: cameraVector.z},
            {x: cameraVector.x, y: cameraVector.y, z: cameraVector.z * -1},
            {x: cameraVector.x * -1, y: cameraVector.y, z: cameraVector.z * -1},
            {x: cameraVector.x * -1, y: cameraVector.y, z: cameraVector.z},
        ];
    }

    if (!cameraTourObj.targetPoints.length) {
        cameraTourObj.targetPoints = [
            {x: cameraVector.x * 0.5, y: cameraVector.y, z: cameraVector.z * 0.5},
            {x: cameraVector.x * 0.5, y: cameraVector.y, z: cameraVector.z * -1 * 0.5},
            {x: cameraVector.x * -1 * 0.5, y: cameraVector.y, z: cameraVector.z * -1 * 0.5},
            {x: cameraVector.x * -1 * 0.5, y: cameraVector.y, z: cameraVector.z * 0.5},
        ];
    }

    createPoints(groupCameraRoute);

    cameraTourObj.groupCameraRoute = groupCameraRoute;

    scene.add(groupCameraRoute);

    return groupCameraRoute;
}

/**
 * Add Transform controls (handle objects on the Scene)
 */

function setTransformControls() {
    const canvas = document.querySelector('#c');
    const {orbitControls, perspectiveCamera: camera} = threeJsVariables;

    const transformControls = new TransformControls(camera, canvas);

    cameraTourObj.canvas = canvas;
    cameraTourObj.transformControls = transformControls;

    canvas.addEventListener('pointerdown', handleAction);
    canvas.addEventListener('pointerup', handleAction);

    transformControls.addEventListener('change', () => orbitControls.enabled = false);
    transformControls.addEventListener('dragging-changed', event => {
        if (!event.value) {
            if (cameraTourObj.cameraCurve) {
                const cameraPoints = cameraTourObj.cameraCurve.getPoints(50);
                cameraTourObj.cameraRouteLine.geometry.setFromPoints(cameraPoints);
                // cameraTourObj.flow.updateCurve( 0, cameraTourObj.cameraCurve );
                setDataToStorage('camera');
            }

            if (cameraTourObj.targetCurve) {
                const pointsTarget = cameraTourObj.targetCurve.getPoints(50);
                cameraTourObj.targetRouteLine.geometry.setFromPoints(pointsTarget);
                // cameraTourObj.flow.updateCurve( 0, cameraTourObj.curveTarget );
                setDataToStorage('target');
            }
        }
    });
}

/**
 * Handler click and touch (Transform controls)
 */

function handleAction(event) {
    if (settingsData.tourIsActive) return;

    let {mouse, orbitControls} = threeJsVariables;

    switch (event.type) {
        case 'pointerdown': {
            cameraTourObj.action = true;
            mouse.x = (event.clientX / cameraTourObj.canvas.clientWidth) * 2 - 1;
            mouse.y = -(event.clientY / cameraTourObj.canvas.clientHeight) * 2 + 1;
            break;
        }
        case 'pointerup': {
            if (!cameraTourObj.action && !orbitControls.enabled) {
                orbitControls.enabled = true;
            }
            break;
        }
    }
}

function createCurveLine(positions, type, group) {
    const line = group.getObjectByName(`${type}Line`);
    const color = (type === 'camera') ? 'yellow' : 'red';

    if (line) {
        line.geometry.dispose();
        line.material.dispose();
        group.remove(line);
    }

    const curve = new THREE.CatmullRomCurve3(
        cameraTourObj[`${type}CurveHandles`].map((handle) => handle.position)
    );

    curve.curveType = "centripetal";
    curve.closed = true;

    const points = curve.getPoints(50);

    const routeLine = new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints(points),
        new THREE.LineBasicMaterial({color: color})
    );

    routeLine.name = `${type}Line`;

    isDev && group.add(routeLine);

    cameraTourObj[`${type}Curve`] = curve;
    cameraTourObj[`${type}RouteLine`] = routeLine;

    setDataToStorage(type);
}

function createPoints(group) {
    const {cameraPoints, targetPoints} = cameraTourObj;
    const positions = isDev
        ? getPositionsFromStorage()
        : {cameraPoints, targetPoints};

    const boxSize = 3;
    const boxGeometry = new THREE.BoxBufferGeometry(boxSize, boxSize, boxSize);

    cameraTourObj.cameraCurveHandles = [];
    cameraTourObj.targetCurveHandles = [];

    const colors = [];

    ['camera', 'target'].forEach(route => {
        const position = positions[`${route}Points`];

        position.forEach((pos, index) => {
            if (route === 'camera') {
                colors.push(Math.random() * 0xffffff);
            }

            const boxMaterial = new THREE.MeshBasicMaterial({color: colors[index]});
            const boxPoint = new THREE.Mesh(boxGeometry, boxMaterial);

            boxPoint.position.set(pos.x, pos.y, pos.z);

            boxPoint.name = `${route}-point-${index}`;
            cameraTourObj[`${route}CurveHandles`].push(boxPoint);
            isDev && group.add(boxPoint);
        });

        createCurveLine(position, route, group);
    });
}

function setCameraDistance() {
    const {orbitControls, trackballControls} = threeJsVariables;

    if (orbitControls.maxDistance !== Infinity) {
        orbitControls.maxDistance = Infinity;
        trackballControls.maxDistance = Infinity;
    } else {
        setMinAndMaxControlsDistance();
    }
}


/**
 * Render update script
 */

export const updateCameraTour = () => {
    if (!config.features.projectTour.active) return;

    if (cameraTourObj.action) {
        const {scene, mouse, raycaster, perspectiveCamera: camera} = threeJsVariables;
        raycaster.setFromCamera(mouse, camera);

        cameraTourObj.action = false;

        const intersects = raycaster.intersectObjects(cameraTourObj.cameraCurveHandles.concat(cameraTourObj.targetCurveHandles));

        if (intersects.length) {
            const target = intersects[0].object;

            if (!target.userData.startCameraBox) {
                cameraTourObj.transformControls.attach(target);
                scene.add(cameraTourObj.transformControls);
            }
        }
    }
}

function moveCameraOnPosition() {
    const {perspectiveCamera: camera} = threeJsVariables;

    const {orbitControlsVector} = getWorldData();
    const {cameraCurveHandles, targetCurveHandles} = cameraTourObj;

    // const cameraDistances = cameraCurveHandles.map(point => cameraVector.distanceTo(point.position));
    // const minDistanceCamera = Math.min(...cameraDistances);
    //
    // const nearestCameraPoint = cameraCurveHandles[cameraDistances.indexOf(minDistanceCamera)].position;
    //
    // const targetDistances = targetCurveHandles.map(point => nearestCameraPoint.distanceTo(point.position));
    // const minDistanceTarget = Math.min(...targetDistances);
    //
    // const nearestTargetPoint = targetCurveHandles[targetDistances.indexOf(minDistanceTarget)].position;

    const nearestCameraPoint = cameraCurveHandles[0].position;
    const nearestTargetPoint = targetCurveHandles[0].position;

    const distFromCameraToStartPoint = camera.position.distanceTo(nearestCameraPoint);

    resetValuesOrbitControls();

    gsap.to(camera.position, {
        duration: distFromCameraToStartPoint > 100 ? 3 : 1,
        x: nearestCameraPoint.x,
        y: nearestCameraPoint.y,
        z: nearestCameraPoint.z,

        onUpdate: () => {
            camera.updateMatrixWorld();
            poiIntersection();
        },

        onComplete: () => {
            cameraTourObj.isCameraOnPosition = true;
            camera.updateMatrixWorld();
            userPrevent(false);

            settingsData.tourIsActive && animateCameraAndTarget();
        },
        onStart: () => {
            userPrevent(true);
        }
    });

    gsap.to(orbitControlsVector, {
        duration: distFromCameraToStartPoint > 100 ? 2 : 1,
        x: nearestTargetPoint.x,
        y: nearestTargetPoint.y,
        z: nearestTargetPoint.z,

        onComplete: () => {
            cameraTourObj.isTargetOnPosition = true;
        }
    })
}

export const runCameraTour = () => {
    if (!cameraTourObj.cameraPositionAnimation && !cameraTourObj.targetPositionAnimation) {
        // let isWidgetActive = document.querySelector('.show-page');
        // if (isWidgetActive) {
        //     isWidgetActive = document.querySelector('.contact-us');
        // }
        // console.log(isWidgetActive);
        //
        // if (isWidgetActive) return;

        autoRotateTimerAction('cancel');
        settingsData.tourIsActive = true;
        settingsData.tourOrbitMode = true;
        moveCameraOnPosition();
    }
}

function animateCameraAndTarget() {
    threeJsVariables.time = new THREE.Clock();

    const {perspectiveCamera: camera} = threeJsVariables;
    const {orbitControlsVector} = getWorldData();

    // cameraTourObj.cameraVelocity = calculateVelocity('camera');
    // cameraTourObj.targetVelocity = calculateVelocity('target');

    // let prevVelCam = cameraTourObj.cameraVelocity[0];
    // let prevVelTarg = cameraTourObj.targetVelocity[0];
    //
    // console.log('cam: ', cameraTourObj.cameraVelocity);
    // console.log('trg: ', cameraTourObj.targetVelocity);

    const cameraVelocity = calculateVelocity('camera');
    const targetVelocity = calculateVelocity('target');

    let prevVelCam = cameraVelocity[0];
    let prevVelTarg = targetVelocity[0];

    gsap.to(camera, {
        duration: 1,
        repeat: -1,

        onStart: setSmoothDistance,
        onUpdate: () => {
            settingsData.last_interaction = Date.now();

            let velocityCamera = checkDistance('camera', cameraVelocity) || prevVelCam;
            let velocityTarget = checkDistance('target', targetVelocity) || prevVelTarg;

            const elapsedTime = threeJsVariables.time.getElapsedTime();
            const cameraSpeed = elapsedTime * (velocityCamera = prevVelCam > velocityCamera ? prevVelCam : velocityCamera);
            const targetSpeed = elapsedTime * (velocityTarget = prevVelTarg > velocityTarget ? prevVelTarg : velocityTarget);

            prevVelCam = velocityCamera;
            prevVelTarg = velocityTarget;

            const camPos = cameraTourObj.cameraCurve.getPoint(cameraSpeed);
            const targetPos = cameraTourObj.targetCurve.getPoint(targetSpeed);

            camera.position.set(camPos.x, camPos.y, camPos.z);
            orbitControlsVector.set(targetPos.x, targetPos.y, targetPos.z);

            camera.updateMatrixWorld();

            poiIntersection();
        }
    });
}

function checkDistance(type, velocityArray) {
    const {perspectiveCamera} = threeJsVariables;

    const points = cameraTourObj[`${type}CurveHandles`];
    const distances = points.map(point => perspectiveCamera.position.distanceTo(point.position));
    const nearestPoint = Math.min(...distances);

    if (nearestPoint < 1) {
        const index = distances.indexOf(nearestPoint);
        return velocityArray[index];
    }
}

function calculateVelocity(type) {
    const array = [];
    const points = cameraTourObj[`${type}CurveHandles`];

    // let time = config.features.projectTour.time[type];
    let time = cameraTourObj.time[type];

    points.forEach((point, index) => {
        const nextPointIndex = (index === points.length - 1) ? 0 : index + 1;
        const distance = point.position.distanceTo(points[nextPointIndex].position);

        const velocity = (distance / time) * 0.001;

        array.push(velocity);
    });

    return array;
}

export const stopCameraTour = (btn) => {
    if (!settingsData.tourIsActive) return;

    const {orbitControlsVector} = getWorldData();

    gsap.killTweensOf(threeJsVariables.perspectiveCamera);
    gsap.killTweensOf(orbitControlsVector);

    settingsData.tourIsActive = false;
    cameraTourObj.cameraPositionAnimation = false;
    cameraTourObj.targetPositionAnimation = false;

    autoRotateTimerAction('update');

    setTargetToDefault();

    if (!btn) {
        btn = document.querySelector('.aside__play__btn');
        btn.classList.toggle('icon-play');
        btn.classList.toggle('icon-pause');
    }
}

function setTargetToDefault() {
    const {orbitControlsVector, cameraVector} = getWorldData();
    const defaultTarget = config.threejs.orbitControls.target;
    const {activate, foreshortening} = config.threejs.orbitControls.autoZoomSettings;

    const dist = activate ? foreshortening : getOrbitControlsDistanceAndPan().maxDistance * 0.5;
    const vector = new THREE.Vector3().copy(cameraVector).normalize().setLength(dist);
    const angle =  getPolarAngleFromViewMode(ViewMode.Orbit).minAngle;

    animateCamera(
        cameraVector,
        vector,
        orbitControlsVector,
        defaultTarget,
        undefined,
        undefined,
        angle,
        null,
        2000,
        TWEEN.Easing.Sinusoidal.InOut,
        onstart,
        onComplete
        );

    function onstart() {
        userPrevent(true);
    }
    function onComplete() {
        cameraTourObj.isTargetOnPosition = true;
        cameraTourObj.targetPositionAnimation = false;

        setMinAndMaxControlsDistance();
        setPolarAngle();
        enableOrbitControls(true);
        setPanLimits();

        threeJsVariables.trackballControls.enabled = true;
        settingsData.tourOrbitMode = false;
        userPrevent(false);
    }

    /*gsap.to(orbitControlsVector, {
        duration: 2,
        x: defaultTarget.x,
        y: defaultTarget.y,
        z: defaultTarget.z,

        onComplete: () => {
            cameraTourObj.isTargetOnPosition = true;
            cameraTourObj.targetPositionAnimation = false;

            setMinAndMaxControlsDistance();
            setPolarAngle();
            enableOrbitControls(true);
            setPanLimits();

            threeJsVariables.trackballControls.enabled = true;
        }
    });*/
}


function resetValuesOrbitControls() {
    const {orbitControls, trackballControls} = threeJsVariables;

    setSmoothDistance();
    enableOrbitControls(false);
    resetPanLimits();
    trackballControls.enabled = false;

    orbitControls.minPolarAngle = 0;
    orbitControls.maxPolarAngle = Math.PI;
}

/**
 * Gui logic and scripts ADD/REMOVE/EXPORT
 */

function exportPoints(route) {
    const obj = cameraTourObj[`${route}CurveHandles`];

    const points = [];

    for (const {position} of obj) {
        points.push(`{
            "x": ${position.x},
            "y": ${position.y},
            "z": ${position.z}
        }`);
    }

    const stringPoints = `[${points.join(',\n')}]`;

    console.log(`${route} points: `, stringPoints);

    prompt(`${route} target: `, stringPoints);
}

function addPoint() {
    const color = Math.random() * 0xffffff;

    ['camera', 'target'].forEach(route => {
        const obj = cameraTourObj[`${route}CurveHandles`];

        const positions = obj.map(item => item.position);
        const lastPoint = positions[positions.length - 1];

        const meshReference = obj.find(item => item.isMesh && !item.userData.startCameraBox);

        const material = meshReference.material.clone();
        console.log(meshReference.material);
        console.log(material);
        material.color = new THREE.Color(color);

        const position = {
            x: lastPoint.x + (1 - Math.random()) * 30,
            y: lastPoint.y,
            z: lastPoint.z + (1 - Math.random()) * 30,
        };

        cameraTourObj[`${route}Points`].push(position);

        const box = new THREE.Mesh(meshReference.geometry, material);
        box.name = `${route} point`;
        box.position.set(position.x, position.y, position.z);

        obj.push(box);

        cameraTourObj.groupCameraRoute.add(box);

        createCurveLine(cameraTourObj[`${route}Points`], route, cameraTourObj.groupCameraRoute);
    });
}

function removePoint() {
    ['camera', 'target'].forEach(route => {
        const obj = cameraTourObj[`${route}CurveHandles`];
        cameraTourObj.transformControls.detach();

        if (obj.length <= 4) return;

        const meshReference = obj.find(item => item.isMesh);
        const lastPoint = obj[obj.length - 1];

        lastPoint.geometry.dispose();
        lastPoint.material.dispose();
        cameraTourObj.groupCameraRoute.remove(lastPoint);

        obj.pop();
        cameraTourObj[`${route}Points`].pop();

        createCurveLine(cameraTourObj[`${route}Points`], route, cameraTourObj.groupCameraRoute);
    });
}

//GUI HELPER
function addGuiHelper() {
    const {gui} = threeJsVariables;
    const timeSpeed = cameraTourObj.time;

    const cameraTour = gui.addFolder('Camera Tour');
    cameraTour.add(cameraTourObj, 'setCameraDistance').name('Infinity zoom');
    cameraTour.add(cameraTourObj, 'addPoint').name('addPoint');
    cameraTour.add(cameraTourObj, 'removePoint').name('remove');
    cameraTour.add(cameraTourObj, 'TargetPoints').name('export target');
    cameraTour.add(cameraTourObj, 'CameraPoints').name('export camera');

    cameraTour.add(timeSpeed, 'camera').min(1).max(60).step(0.1).name('Camera Speed');
    cameraTour.add(timeSpeed, 'target').min(1).max(60).step(0.1).name('Target Speed');

    // cameraTour.add(timeSpeed, 'camera').min(1).max(60).step(0.1).name('Camera Speed').onFinishChange(() => {
    //     console.log('New camera value', timeSpeed.camera);
    //     cameraTourObj.cameraVelocity = calculateVelocity('camera');
    // });
    // cameraTour.add(timeSpeed, 'target').min(1).max(60).step(0.1).name('Target Speed').onFinishChange(() => {
    //     console.log('New target value', timeSpeed.target);
    //     cameraTourObj.targetVelocity = calculateVelocity('target');
    // });
}

/**
 * Load points and settings to Session storage
 */

function setDataToStorage() {

    sessionStorage.removeItem(`tourPositions_${cameraTourObj.projectName}`);

    const cameraPoints = getPositions('camera');
    const targetPoints = getPositions('target');

    const points = {
        camera: cameraPoints,
        target: targetPoints
    }

    sessionStorage.setItem(`tourPositions_${cameraTourObj.projectName}`, JSON.stringify(points));
}

function getPositionsFromStorage() {
    const {cameraVector} = getWorldData();

    const pointsStorage = sessionStorage.getItem(`tourPositions_${cameraTourObj.projectName}`);

    const cameraPoints = pointsStorage ? JSON.parse(pointsStorage).camera : cameraTourObj.cameraPoints;
    const targetPoints = pointsStorage ? JSON.parse(pointsStorage).target : cameraTourObj.targetPoints;

    cameraPoints[0] = {
        x: cameraVector.x,
        y: cameraVector.y,
        z: cameraVector.z,
    }

    return {
        cameraPoints,
        targetPoints
    };
}

function getPositions(type) {
    const obj = cameraTourObj[`${type}CurveHandles`];

    const positions = [];

    for (const {position} of obj) {
        positions.push({
            x: position.x,
            y: position.y,
            z: position.z,
        });
    }

    return positions;
}

export const showHideButtonPlay = (action) => {
    if (!config.features?.projectTour?.active && config.features?.blockCylinderMode) return;

    const btn = document.querySelector('.aside__play__btn');

    if (!btn) return;

    const isHidden = btn.classList.contains('hide');

    switch (action) {
        case 'show': {
            isHidden && btn.classList.remove('hide');
            break;
        }
        case 'hide': {
            !isHidden && btn.classList.add('hide');
            break;
        }
    }
}